/**
 * Small helpers for the interface layer: escaped HTML, the line icons inlined so they take the
 * theme's colours, and the logo files. Decision 0013, `docs/design/ui.md`.
 */
import back from "@mistsweeper/assets/icons/back.svg?raw";
import banner from "@mistsweeper/assets/icons/banner.svg?raw";
import cabin from "@mistsweeper/assets/icons/cabin.svg?raw";
import chevronDown from "@mistsweeper/assets/icons/chevron-down.svg?raw";
import chevronRight from "@mistsweeper/assets/icons/chevron-right.svg?raw";
import close from "@mistsweeper/assets/icons/close.svg?raw";
import compass from "@mistsweeper/assets/icons/compass.svg?raw";
import external from "@mistsweeper/assets/icons/external.svg?raw";
import menu from "@mistsweeper/assets/icons/menu.svg?raw";
import turnLeft from "@mistsweeper/assets/icons/turn-left.svg?raw";
import turnRight from "@mistsweeper/assets/icons/turn-right.svg?raw";
import zoomIn from "@mistsweeper/assets/icons/zoom-in.svg?raw";
import zoomOut from "@mistsweeper/assets/icons/zoom-out.svg?raw";
import logoMistUrl from "@mistsweeper/assets/logo/mistsweeper-logo-mist.svg?url";
import logoSpacedUrl from "@mistsweeper/assets/logo/mistsweeper-logo-spaced-mist.svg?url";

const ICONS = {
  back,
  banner,
  cabin,
  chevronDown,
  chevronRight,
  close,
  compass,
  external,
  menu,
  turnLeft,
  turnRight,
  zoomIn,
  zoomOut,
} as const;

export type IconName = keyof typeof ICONS;

/** Text made safe to put in HTML. */
export function esc(text: string | number): string {
  return String(text)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/**
 * An icon as inline SVG at a size in pixels, hidden from assistive technology (the control
 * carries the label). The 2-unit stroke is scaled to stay 2 px on screen.
 */
export function icon(name: IconName, size = 24, cls = ""): string {
  const grid = name === "compass" ? 32 : 24;
  const stroke = name === "compass" ? null : (2 * grid) / size;
  let svg = ICONS[name].replace(
    "<svg ",
    `<svg class="icon ${cls}" aria-hidden="true" focusable="false" `,
  );
  svg = svg.replace(/width="\d+" height="\d+"/, `width="${size}" height="${size}"`);
  if (stroke !== null) svg = svg.replace('stroke-width="2"', `stroke-width="${stroke}"`);
  return svg;
}

/**
 * The wordmark: the mist version for large sizes, and where it is small the spaced one, whose
 * letters stand further apart so they stay apart (Daniel, 2026-10-17). The file is a mask over the
 * ink, so the wordmark takes the theme's ink like the text around it.
 */
export function logo(kind: "mist" | "spaced", alt: string, cls = ""): string {
  const src = logoUrl(kind);
  return `<span class="logo logo-${kind} ${cls}" role="img" aria-label="${esc(alt)}" style="--logo: url('${src}')"></span>`;
}

/** The wordmark's file, black on clear, for drawing it into a picture. */
export function logoUrl(kind: "mist" | "spaced"): string {
  return kind === "mist" ? logoMistUrl : logoSpacedUrl;
}

/** An element from an HTML string; the string must have one root. */
export function fragment<T extends HTMLElement = HTMLElement>(html: string): T {
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  return t.content.firstElementChild as T;
}

/** Sets the text of every element bound to a name under a root. */
export function bind(root: ParentNode, name: string, text: string): void {
  for (const node of root.querySelectorAll<HTMLElement>(`[data-bind="${name}"]`)) {
    if (node.textContent !== text) node.textContent = text;
  }
}

/** Sets aria-pressed on every element that acts a name under a root. */
export function press(root: ParentNode, action: string, on: boolean): void {
  for (const node of root.querySelectorAll<HTMLElement>(`[data-action="${action}"]`)) {
    node.setAttribute("aria-pressed", String(on));
  }
}

/** The accent square that leads a heading. */
export const SQUARE = '<span class="square" aria-hidden="true"></span>';

/**
 * A mouse wheel moves a row that scrolls sideways while the pointer is over it (Daniel,
 * 2026-10-19: with a mouse and no trackpad the drawer could not be scrolled on desktop). A
 * trackpad's sideways swipe scrolls it as before, and a pinch is left to the browser.
 */
export function wheelSideways(row: HTMLElement): void {
  row.addEventListener(
    "wheel",
    (e) => {
      if (e.ctrlKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (row.scrollWidth <= row.clientWidth) return;
      // Lines and pages to pixels, roughly, as the world's wheel does.
      const unit = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? row.clientWidth : 1;
      row.scrollLeft += e.deltaY * unit;
      e.preventDefault();
    },
    { passive: false },
  );
}
